import React, { useState, useEffect, useRef, forwardRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/context/AuthContext";
import { EnvelopeSimple, Eye, EyeSlash, ArrowLeft, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { Button, Checkbox, cn } from "../../components/ui";

const BRAND_NAME = "Technifro";
const LOGO = "/logotechnifro.png";

/* ---------- themed field helpers ---------- */
const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink">{children}</label>
);

const ThemedInput = forwardRef(({ icon: Icon, trailing, className = "", ...props }, ref) => (
  <div className="relative">
    {Icon ? <Icon size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-faint" /> : null}
    <input
      ref={ref}
      className={cn(
        "h-12 w-full rounded-xl border border-line bg-surface-2 text-ink placeholder:text-faint outline-none transition",
        "focus:border-accent focus:ring-2 focus:ring-accent/25",
        Icon ? "pl-11" : "pl-4",
        trailing ? "pr-11" : "pr-4",
        className
      )}
      {...props}
    />
    {trailing ? <div className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</div> : null}
  </div>
));
ThemedInput.displayName = "ThemedInput";

const SubtleError = ({ message }) =>
  message ? (
    <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400" role="alert" aria-live="assertive">
      {message}
    </div>
  ) : null;

/* ---------- Page ---------- */
function Login() {
  const [view, setView] = useState("signin"); // 'signin' | 'forgot'
  const [loginData, setLoginData] = useState({ email: "", password: "", code: "" });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [codeVerify, setCodeVerify] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      setErrorMessage("Email and password are required.");
      return;
    }
    try {
      setErrorMessage("");
      setLoading(true);
      const { data } = await axiosInstance.post("/auth/login", { email: loginData.email, password: loginData.password });
      if (data && data.success) {
        setCodeVerify(true);
      } else if (data && data.user) {
        await login();
        navigate("/");
      } else {
        setErrorMessage("Unexpected response. Please try again.");
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeVerification = async (e) => {
    e.preventDefault();
    if (!loginData.code || loginData.code.length !== 6) {
      setErrorMessage("Please enter the 6-digit code.");
      return;
    }
    try {
      setErrorMessage("");
      setLoading(true);
      const { data } = await axiosInstance.post("/auth/code-verification", { email: loginData.email, password: loginData.password, code: loginData.code });
      if (data && data.user) {
        setCodeVerify(false);
        await login();
        navigate("/");
      } else {
        setErrorMessage("Invalid code. Please try again.");
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!loginData.email) {
      setErrorMessage("Please enter your email first.");
      return;
    }
    try {
      setErrorMessage("");
      setLoading(true);
      const { data } = await axiosInstance.post("/auth/forget-passKey", { email: loginData.email });
      if (data) setCodeVerify(true);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPassCodeVerification = async (e) => {
    e.preventDefault();
    if (!loginData.code || loginData.code.length !== 6) {
      setErrorMessage("Please enter the 6-digit code.");
      return;
    }
    try {
      setErrorMessage("");
      setLoading(true);
      const { data } = await axiosInstance.post("/auth/verify-code", { email: loginData.email, code: loginData.code });
      if (data) {
        setCodeVerify(false);
        setView("signin");
      } else {
        setErrorMessage("Invalid code. Please try again.");
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid h-screen w-screen grid-cols-1 overflow-hidden bg-bg text-ink text-start lg:grid-cols-2">
      {/* Left — branded panel */}
      <div className="relative hidden overflow-hidden bg-sidebar text-white lg:flex lg:flex-col lg:justify-between">
        <div className="pointer-events-none absolute inset-0 pattern-dots opacity-70" />
        <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />

        <div className="relative p-10">
          <img src={LOGO} alt={BRAND_NAME} className="h-12 w-auto" />
        </div>

        <div className="relative px-20">
          <h2 className="max-w-xl text-6xl font-[400] leading-tight">
            Pilotez votre boutique avec exigence.
          </h2>
          <p className="mt-3 max-w-md text-md text-white/60">
            Catalogue, devis, commandes et contenu — réunis dans un espace clair et rapide.
          </p>
          <ul className="mt-8 grid max-w-md grid-cols-2 gap-x-6 gap-y-3 text-sm text-white/80">
            {["Produits & variantes", "Demandes de devis", "Commandes & clients", "SEO & contenu"].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <CheckCircle size={18} weight="fill" className="shrink-0 text-accent" /> {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative p-10 text-xs text-white/40">
          © {new Date().getFullYear()} {BRAND_NAME}. Espace d'administration.
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          {/* mobile brand */}
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <img src={LOGO} alt={BRAND_NAME} className="h-10 w-auto" />
          </div>

          {view === "signin" && !codeVerify && (
            <SignIn loginData={loginData} setLoginData={setLoginData} loading={loading} errorMessage={errorMessage} handleLogin={handleLogin} setView={setView} />
          )}
          {view === "forgot" && !codeVerify && (
            <ForgotPassword loginData={loginData} setLoginData={setLoginData} loading={loading} errorMessage={errorMessage} handleForgotPassword={handleForgotPassword} setView={setView} />
          )}
          {codeVerify && (
            <VerificationCode
              loginData={loginData}
              setLoginData={setLoginData}
              loading={loading}
              errorMessage={errorMessage}
              handleCodeVerification={view === "forgot" ? handleForgetPassCodeVerification : handleCodeVerification}
              setView={setView}
              setCodeVerify={setCodeVerify}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- Sign In ---------- */
function SignIn({ loginData, setLoginData, loading, errorMessage, handleLogin, setView }) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [remember, setRemember] = useState(true);

  return (
    <form onSubmit={handleLogin} className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Bon retour 👋</h1>
        <p className="mt-1 text-sm text-muted">Connectez-vous à l'espace d'administration {BRAND_NAME}.</p>
      </div>

      <SubtleError message={errorMessage} />

      <div className="space-y-4">
        <div>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <ThemedInput
            id="email" name="email" type="email" placeholder="vous@technifro.ma"
            value={loginData.email}
            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
            icon={EnvelopeSimple} autoComplete="email" inputMode="email" required
          />
        </div>

        <div>
          <FieldLabel htmlFor="password">Mot de passe</FieldLabel>
          <ThemedInput
            id="password" name="password" type={passwordVisible ? "text" : "password"} placeholder="••••••••••••"
            value={loginData.password}
            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            autoComplete="current-password" required
            trailing={
              <button type="button" onClick={() => setPasswordVisible((v) => !v)} aria-label={passwordVisible ? "Masquer" : "Afficher"} className="grid h-9 w-9 place-items-center rounded-lg text-faint transition hover:bg-surface hover:text-ink">
                {passwordVisible ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            }
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Checkbox checked={remember} onChange={setRemember} label="Se souvenir de moi" size={16} />
        <button type="button" onClick={() => setView("forgot")} className="text-[13px] font-medium text-accent transition hover:text-accent-strong">
          Mot de passe oublié ?
        </button>
      </div>

      <Button type="submit" size="lg" loading={loading} className="w-full">Se connecter</Button>
    </form>
  );
}

/* ---------- Forgot Password ---------- */
function ForgotPassword({ loginData, setLoginData, loading, errorMessage, handleForgotPassword, setView }) {
  return (
    <form onSubmit={handleForgotPassword} className="space-y-6">
      <BackLink onClick={() => setView("signin")} />
      <div>
        <h1 className="text-2xl font-semibold text-ink">Mot de passe oublié</h1>
        <p className="mt-1 text-sm text-muted">Saisissez votre email pour recevoir un code de vérification.</p>
      </div>

      <SubtleError message={errorMessage} />

      <div>
        <FieldLabel htmlFor="fp-email">Email</FieldLabel>
        <ThemedInput
          id="fp-email" name="email" type="email" placeholder="vous@technifro.ma"
          value={loginData.email}
          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
          icon={EnvelopeSimple} autoComplete="email" inputMode="email" required
        />
      </div>

      <Button type="submit" size="lg" loading={loading} className="w-full">Envoyer le code</Button>
    </form>
  );
}

/* ---------- Verification Code (OTP) ---------- */
function VerificationCode({ loginData, setLoginData, loading, errorMessage, handleCodeVerification, setView, setCodeVerify }) {
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  const handleInput = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 1) {
      const next = [...verificationCode];
      next[index] = value;
      setVerificationCode(next);
      setLoginData({ ...loginData, code: next.join("") });
      if (value && index < 5) inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const txt = e.clipboardData.getData("text").replace(/\D/g, "");
    if (txt.length === 6) {
      const arr = txt.split("");
      setVerificationCode(arr);
      setLoginData({ ...loginData, code: txt });
      inputRefs.current[5]?.focus();
    }
  };

  return (
    <form onSubmit={handleCodeVerification} className="space-y-6">
      <button type="button" onClick={() => { setCodeVerify(false); setView("signin"); }} className="inline-flex items-center gap-1.5 text-sm text-muted transition hover:text-ink">
        <ArrowLeft size={16} /> Retour
      </button>

      <div>
        <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-accent-soft text-accent">
          <ShieldCheck size={24} weight="duotone" />
        </div>
        <h1 className="text-2xl font-semibold text-ink">Vérification</h1>
        <p className="mt-1 text-sm text-muted">Saisissez le code à 6 chiffres envoyé à votre email.</p>
      </div>

      <SubtleError message={errorMessage} />

      <div className="grid grid-cols-6 gap-2">
        {verificationCode.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            inputMode="numeric" pattern="[0-9]*" type="text" maxLength={1}
            value={digit}
            onChange={(e) => handleInput(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            aria-label={`Chiffre ${index + 1}`}
            className="h-14 w-full rounded-xl border border-line bg-surface-2 text-center text-lg font-semibold text-ink outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/25"
          />
        ))}
      </div>

      <Button type="submit" size="lg" loading={loading} disabled={verificationCode.join("").length !== 6} className="w-full">
        Vérifier
      </Button>
    </form>
  );
}

const BackLink = ({ onClick }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm text-muted transition hover:text-ink">
    <ArrowLeft size={16} /> Retour à la connexion
  </button>
);

export default Login;
